import type { Product, PropertyValue } from '@skop-app/lib-domain/types'
import { relationFields } from '@skop-app/lib-domain/constants'

import type { Context } from '@/types/grid'
import type {
  AdaptedProduct,
  CatalogItem,
  DynamicKeys,
  Property,
} from '@/types'
import {
  type DimensionSource,
  formatDimensionSummary,
  type FormattedDimensionLabels,
} from '@/domain/product/persistence/dimensionRules'
import { formulaOverride } from './formula.helpers'
import { formatNumber } from './helpers'
import { matchKey } from './matchKey.utils'
import {
  extractIdOrValue,
  extractLabel,
  hasLabelField,
  isRecord,
} from './value.utils'

/**
 * @deprecated Legacy V1 product persistence helpers.
 * Prefer the V2 rules + inventory setters/handlers flow.
 */

export const removeEmptyFields = (item: Product) =>
  Object.keys(item).forEach((key) =>
    item[key] === null ||
    item[key] === undefined ||
    item[key] === '' ||
    (Array.isArray(item[key]) && item[key].length === 0)
      ? delete item[key]
      : null,
  )

// Convert object to flat key
// ie. dimension: { width: 10, height: 20 } => dimension.width: 10, dimension.height: 20
export const flattenObjectKeys = (item: Product) =>
  Object.keys(item).forEach((key) => {
    const value = item[key]
    if (
      value &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      !relationFields.includes(key)
    ) {
      Object.keys(value).forEach((subKey) => {
        const subValue = value[subKey]
        item[`${key}.${subKey}`] = subValue
      })
      delete item[key]
    }
  })

// Helper function to safely convert to number
const safeParseFloat = (value: string): number | string => {
  const parsed = parseFloat(value)
  return !isNaN(parsed) ? parsed : value
}

/**
 * @deprecated Legacy adapter helper used by V1 flows.
 */
export const formatPropertyValues = (
  properties?: {
    key: string
    values: any[]
    type: PropertyValue
  }[],
) =>
  properties
    ?.filter(({ key }) => key !== 'quantity.number')
    ?.map(({ key, values, type }) => {
      // Keep as an array
      if (type === 'LIST' || key === 'pictures') {
        return {
          [key]: values,
        }
      }
      // Convert to number
      if (
        key.includes('dimension') ||
        key.includes('weight') ||
        key.includes('quantity') ||
        key.includes('price')
      ) {
        return {
          [key]: safeParseFloat(values?.[0] ?? ''),
        }
      }
      // Convert to string
      return {
        [key]: values?.[0] ?? '',
      }
    })

const isEmptyValue = (value: unknown): boolean =>
  value === null || value === undefined || value === ''

// Reconstruction of an object from similar fields (e.g. quantity.number, quantity.unit) => { quantity: { number: 10, unit: 'kg' } }
const handleNestedFields = (
  data: AdaptedProduct,
  field: string,
  options?: Property[],
) => {
  const fieldParts = field.split('.')

  const [rootKey] = fieldParts
  const relatedFields = Object.keys(data).filter((f) =>
    f.startsWith(`${rootKey}.`),
  )
  const aggregatedValues = relatedFields.reduce((acc, f) => {
    const [_, subKey] = f.split('.')
    // Skip custom/auxiliary nested keys (price.amount_1, etc.)
    if (!subKey || subKey.includes('_')) {
      return acc
    }
    const fieldValue = data[f]
    const fieldLabel = hasLabelField(fieldValue) ? fieldValue.label : undefined
    // Nested fields values aren't arrays
    const value = Array.isArray(fieldLabel) ? fieldLabel[0] : fieldLabel
    // can't be empty
    // Keep 0 (and false) as valid values; only drop null/undefined/''
    if (isEmptyValue(value)) return acc
    // Might need to be converted to a label (unit)
    if (options) {
      const optionValue = matchKey(value, options)
      return { ...acc, [subKey]: optionValue?.label || value }
    }
    return { ...acc, [subKey]: value }
  }, {} as DynamicKeys)

  return { [rootKey]: aggregatedValues }
}

export const getField = (
  data: AdaptedProduct,
  field: string,
  options?: Property[],
) => handleNestedFields(data, field, options)?.[field]

export const getQuantityLabel = (product: AdaptedProduct): string => {
  const batchedQuantity = getField(product, 'batched_quantity')
  const quantity = getField(product, 'quantity')

  const quantityRecord = isRecord(quantity) ? quantity : undefined
  const batchedRecord = isRecord(batchedQuantity) ? batchedQuantity : undefined
  const unit =
    quantityRecord && typeof quantityRecord.unit === 'string'
      ? quantityRecord.unit
      : ''

  const batchedNumber = batchedRecord?.number
  const quantityNumber = quantityRecord?.number
  const rawNumber =
    (typeof batchedNumber === 'number' || typeof batchedNumber === 'string'
      ? batchedNumber
      : undefined) ??
    (typeof quantityNumber === 'number' || typeof quantityNumber === 'string'
      ? quantityNumber
      : undefined)

  const number = formatNumber(rawNumber)
  return number ? `${number} ${unit}` : ''
}

export const getProductCustomId = (element: any): string => {
  const customIDKey = Object.keys(element).find((key) => element[key]?.isID)
  const customID = customIDKey && element[customIDKey]?.label
  return (customID && (formulaOverride(customID) || customID)) || ''
}

export const getNameWithCustomID = (element: any): string => {
  const name = element.name?.label || element.label || ''
  const customID = getProductCustomId(element)
  if (name && customID.length) return customID + ' - ' + name
  return name
}

const getProductNameWithMaterials = (product: AdaptedProduct): string => {
  if (!product) return ''

  try {
    const name = product.name?.label || ''

    // Check if we should display material
    const shouldDisplayMaterial = product.custom_boolean_1?.value === 'true'

    // Get materials
    let materials = ''
    if (Array.isArray(product?.material)) {
      materials = product.material
        .map((m) => m?.label)
        .filter(Boolean)
        .join(', ')
    }

    // Combine name and materials if needed
    if (name && shouldDisplayMaterial && materials) {
      return name + ' - ' + materials
    }

    return name
  } catch (error) {
    console.error('Error in getProductNameWithMaterials:', error, product)
    return product?.name?.label || ''
  }
}

// Prepend the key with the locale
export const getTranslationKey = (key: string, locale: string) =>
  `${key}${locale === 'fr' ? '' : locale.toUpperCase()}`

// Get the translation based on the locale
export const getTranslation = (
  item: CatalogItem | Product,
  key: string,
  locale: string,
  fallbackLocales?: readonly string[],
) => {
  if (!item) return ''

  try {
    const localesToTry =
      fallbackLocales && fallbackLocales.length > 0
        ? fallbackLocales
        : locale === 'en'
          ? ['en', 'fr']
          : locale === 'nl'
            ? ['nl', 'fr']
            : ['fr', 'en', 'nl']

    for (const loc of localesToTry) {
      const translationKey = getTranslationKey(key, loc)
      const value = extractLabel(item?.[translationKey])
      if (value) return value
    }

    return ''
  } catch (error) {
    console.error('Error in getTranslation:', error, { item, key, locale })
    return ''
  }
}

export const getCustomProductName = (
  product: AdaptedProduct,
  locale: string,
  fallbackLocales?: readonly string[],
): string => {
  if (!product) return ''

  try {
    const productName = getProductNameWithMaterials(product)
    const complement = getTranslation(
      product,
      'complement',
      locale,
      fallbackLocales,
    )

    if (complement?.length) {
      return productName + ' - ' + complement
    }

    return productName
  } catch (error) {
    console.error('Error in getCustomProductName:', error, { product, locale })
    // Fallback to simple name access
    return product?.name?.label || product?.productID || 'Unnamed Product'
  }
}

/**
 * Gives formatted numbers and labels for dimensions
 * @param source The object containing dimension fields and/or dimensionRange
 * @returns The formatted numbers and labels
 */
export const formatProductDimensions = (
  source?: DimensionSource,
): FormattedDimensionLabels | null => formatDimensionSummary(source)

export const formatDimensions_Rotor = formatProductDimensions

// Utility function to safely extract ID/value from various formats
export const extractValue = (
  value: string | { value?: string; label?: string; id?: string } | undefined,
): string => {
  const extracted = extractIdOrValue(value)
  return extracted === undefined ? '' : String(extracted)
}

type LookupReferences = Parameters<typeof matchKey>[1]
type LookupOptionsByID = Record<
  string,
  { label?: string; value?: unknown; id?: string }
>

export const resolveLookupLabel = ({
  value,
  references,
  optionsByID,
  refData,
  fallbackValue,
}: {
  value: string | number | undefined
  references?: LookupReferences
  optionsByID?: LookupOptionsByID
  refData?: Record<string, string>
  fallbackValue?: string
}): string => {
  const normalizedValue =
    value === undefined || value === null ? '' : String(value)

  if (!normalizedValue) return fallbackValue || ''

  const directLabel = optionsByID?.[normalizedValue]?.label
  if (typeof directLabel === 'string' && directLabel.length > 0) {
    return directLabel
  }

  const property =
    Array.isArray(references) && references.length > 0
      ? matchKey(normalizedValue, references, optionsByID)
      : undefined
  const propertyLabel =
    typeof property?.label === 'string' && property.label.length > 0
      ? property.label
      : undefined

  if (propertyLabel && property?.review !== true) {
    return propertyLabel
  }

  const refDataLabel = refData?.[normalizedValue]
  if (typeof refDataLabel === 'string' && refDataLabel.length > 0) {
    return refDataLabel
  }

  if (property?.review === true) {
    return fallbackValue || ''
  }

  return propertyLabel || fallbackValue || ''
}

// Utility function to create text labels for lot and category nodes
export const createNodeText = (
  value: string | undefined,
  categoryOptions:
    | NonNullable<Context['propertyOptions']>['category']
    | undefined,
  categoryOptionsByID: Context['optionsByID']['category'] | undefined,
  type: 'lot' | 'category',
  fallbackValue?: string,
  categoryRefData?: NonNullable<Context['refDataByKey']>['category'],
): string => {
  try {
    if (!value) return fallbackValue || ''

    const hasOptions =
      Array.isArray(categoryOptions) && categoryOptions.length > 0
    const matchedProperty = hasOptions
      ? matchKey(value, categoryOptions, categoryOptionsByID)
      : undefined
    const property =
      matchedProperty?.review === true ? undefined : matchedProperty
    const label = resolveLookupLabel({
      value,
      references: categoryOptions,
      optionsByID: categoryOptionsByID,
      refData: categoryRefData,
      fallbackValue,
    })

    let index = ''
    if (type === 'lot') {
      index = property?.value?.toString() || ''
    } else if (type === 'category') {
      const categoryIndex = Number(property?.value?.toString().split('-')?.[0])
      index = !isNaN(categoryIndex) ? categoryIndex.toString() : ''
    }

    return (index ? `${index}. ${label}` : label).toLocaleUpperCase()
  } catch (error) {
    console.warn('Error in createNodeText:', error, {
      value,
      categoryOptions,
      type,
      fallbackValue,
      categoryRefData,
    })
    return fallbackValue || ''
  }
}
